import React, { useEffect, useRef, useCallback } from 'react'

// Removed prism colors - using electric blue/purple scheme instead

/**
 * AudioVisualizer component - Real-time audio visualization with LED bars or circular pattern
 *
 * @param {Object} props
 * @param {MediaStream|null} props.audioStream - Audio stream to visualize
 * @param {boolean} props.isMuted - Whether the audio is muted
 * @param {string} props.className - CSS class name for the container
 * @param {number} props.width - Canvas width
 * @param {number} props.height - Canvas height
 * @param {boolean} props.isCoachStream - Whether this is for coach stream (affects colors)
 * @param {boolean} props.showWhenMuted - Whether to show visualization when muted
 * @param {string} props.visualizationMode - Visualization mode: 'bars', 'circular', 'knightRider', 'particles', 'bezier', 'radial', 'energy', 'tunnel', 'grid', 'fire'
 */
const AudioVisualizer = ({
  audioStream,
  isMuted = false,
  className = '',
  width = 145,
  height = 150,
  isCoachStream = false,
  showWhenMuted = false,
  visualizationMode = 'bars', // 'bars', 'circular', 'knightRider', 'particles', 'bezier', 'radial', 'energy', 'tunnel', 'grid', 'fire'
}) => {
  const canvasRef = useRef(null)
  const audioContextRef = useRef(null)
  const analyserRef = useRef(null)
  const sourceNodeRef = useRef(null)
  const animationRef = useRef(null)

  // Particle system state
  const particlesRef = useRef([])
  const timeRef = useRef(0)

  // LED bars configuration (based on audioMotion-analyzer mode 3)
  const LED_COUNT = 96
  const LED_SPACE_V = 6
  const LED_SPACE_H = 0.225
  const UNLIT_COLOR = '#7f7f7f22'

  // Peak hold system
  const peakHoldRef = useRef(new Array(16).fill(0))
  const peakAlphaRef = useRef(new Array(16).fill(0))
  const _peakHoldTime = 500 // ms
  const peakFadeTime = 750 // ms
  const gravity = 3.8

  // Noise gate system - tuned for dramatic mid-range visualization
  const noiseFloorRef = useRef(new Array(16).fill(0))
  const speechThreshold = 0.15 // Balanced threshold between original (0.25) and more sensitive (0.05)
  const noiseFloorDecay = 0.92 // Slower decay for more stable gating
  const maxThreshold = 0.85 // Maximum threshold to ignore overly loud sounds
  const dramaticBoost = 2.5 // Multiplier for dramatic visualization of mid-range sounds

  // Calculate frequency bands (16 bands with better distribution)
  const calculateFrequencyBands = useCallback((fftData, sampleRate) => {
    const bands = []
    const minFreq = 80
    const maxFreq = 8000
    const bandCount = 16

    // Calculate frequency for each band using logarithmic scale
    for (let i = 0; i < bandCount; i++) {
      const freq = minFreq * Math.pow(maxFreq / minFreq, i / (bandCount - 1))
      const binIndex = Math.floor((freq / sampleRate) * fftData.length * 2)

      if (binIndex < fftData.length) {
        // Average multiple bins for smoother response
        const binRange = Math.max(1, Math.floor(fftData.length / bandCount / 4))
        let sum = 0
        let count = 0

        for (
          let j = Math.max(0, binIndex - binRange);
          j <= Math.min(fftData.length - 1, binIndex + binRange);
          j++
        ) {
          sum += fftData[j]
          count++
        }

        const avgValue = sum / count / 255

        // Update noise floor (adaptive threshold)
        const currentNoiseFloor = noiseFloorRef.current[i]
        if (avgValue < currentNoiseFloor) {
          // Slowly lower noise floor when signal is quiet
          noiseFloorRef.current[i] = currentNoiseFloor * noiseFloorDecay
        } else {
          // Quickly raise noise floor when signal is loud
          noiseFloorRef.current[i] = Math.min(
            currentNoiseFloor * 1.1,
            avgValue * 0.8,
          )
        }

        // Apply noise gate - only show activity above noise floor + threshold
        const effectiveThreshold = Math.max(
          speechThreshold,
          currentNoiseFloor + 0.05,
        )

        // Filter out overly loud sounds
        if (avgValue > maxThreshold) {
          bands.push({
            freq,
            binIndex,
            value: 0,
          })
          continue
        }

        // Only process sounds in the significant mid-range
        if (avgValue <= effectiveThreshold) {
          bands.push({
            freq,
            binIndex,
            value: 0,
          })
          continue
        }

        // Normalize the mid-range values for dramatic visualization
        const normalizedValue =
          (avgValue - effectiveThreshold) / (maxThreshold - effectiveThreshold)

        // Apply dramatic boost to make mid-range sounds fill the spectrum
        const boostedValue = Math.min(1, normalizedValue * dramaticBoost)

        bands.push({
          freq,
          binIndex,
          value: boostedValue,
        })
      }
    }

    return bands
  }, [])

  // Get color based on stream type and band position
  const getStreamColor = useCallback(
    (_amplitude, bandIndex = 0) => {
      if (isCoachStream) {
        // Coach stream - green to blue gradient (current candidate colors)
        const colors = [
          'rgba(34, 197, 94, 0.8)', // green-500
          'rgba(16, 185, 129, 0.8)', // emerald-500
          'rgba(6, 182, 212, 0.8)', // cyan-500
          'rgba(14, 165, 233, 0.8)', // sky-500
          'rgba(59, 130, 246, 0.8)', // blue-500
          'rgba(99, 102, 241, 0.8)', // indigo-500
          'rgba(139, 92, 246, 0.8)', // violet-500
          'rgba(168, 85, 247, 0.8)', // purple-500
          'rgba(192, 132, 252, 0.8)', // purple-400
          'rgba(196, 181, 253, 0.8)', // purple-300
          'rgba(221, 214, 254, 0.8)', // purple-200
          'rgba(233, 213, 255, 0.8)', // purple-100
          'rgba(245, 243, 255, 0.8)', // violet-50
          'rgba(248, 250, 252, 0.8)', // slate-50
          'rgba(251, 252, 253, 0.8)', // gray-50
          'rgba(255, 255, 255, 0.8)', // white
        ]
        return colors[Math.min(bandIndex, colors.length - 1)]
      } else {
        // Local stream - yellow to orange to red gradient
        const colors = [
          'rgba(254, 240, 138, 0.8)', // yellow-200
          'rgba(253, 224, 71, 0.8)', // yellow-400
          'rgba(250, 204, 21, 0.8)', // yellow-500
          'rgba(245, 158, 11, 0.8)', // amber-500
          'rgba(251, 146, 60, 0.8)', // orange-400
          'rgba(249, 115, 22, 0.8)', // orange-500
          'rgba(234, 88, 12, 0.8)', // orange-600
          'rgba(239, 68, 68, 0.8)', // red-500
          'rgba(220, 38, 38, 0.8)', // red-600
          'rgba(185, 28, 28, 0.8)', // red-700
          'rgba(153, 27, 27, 0.8)', // red-800
          'rgba(127, 29, 29, 0.8)', // red-900
          'rgba(69, 10, 10, 0.8)', // red-950
          'rgba(45, 7, 7, 0.8)', // dark red
          'rgba(25, 4, 4, 0.8)', // darker red
          'rgba(12, 2, 2, 0.8)', // darkest red
        ]
        return colors[Math.min(bandIndex, colors.length - 1)]
      }
    },
    [isCoachStream],
  )

  // Calculate LED position for a given amplitude
  const getLEDPosition = useCallback((amplitude) => {
    const ledIndex = Math.floor(amplitude * LED_COUNT)
    return Math.max(0, ledIndex * (LED_SPACE_V + 2) - LED_SPACE_V)
  }, [])

  // Draw bar visualization (original LED bars)
  const drawBarVisualization = useCallback(
    (
      canvasCtx,
      bands,
      _canvasWidth,
      canvasHeight,
      barWidth,
      barSpacing,
      actualBarWidth,
    ) => {
      bands.forEach((band, index) => {
        const x = index * barWidth + barSpacing / 2
        const amplitude = band.value

        // Calculate LED height
        const ledHeight = getLEDPosition(amplitude)
        const peakHeight = getLEDPosition(peakHoldRef.current[index])

        // Draw unlit LEDs (background)
        canvasCtx.strokeStyle = UNLIT_COLOR
        canvasCtx.lineWidth = 1
        canvasCtx.beginPath()
        canvasCtx.moveTo(
          x + actualBarWidth / 2,
          canvasHeight - LED_COUNT * (LED_SPACE_V + 2),
        )
        canvasCtx.lineTo(x + actualBarWidth / 2, canvasHeight)
        canvasCtx.stroke()

        // Draw lit LEDs
        if (amplitude > 0) {
          const color = getStreamColor(amplitude, index)
          canvasCtx.strokeStyle = color
          canvasCtx.lineWidth = actualBarWidth
          canvasCtx.beginPath()
          canvasCtx.moveTo(x + actualBarWidth / 2, canvasHeight)
          canvasCtx.lineTo(x + actualBarWidth / 2, canvasHeight - ledHeight)
          canvasCtx.stroke()
        }

        // Draw peak hold
        if (peakHeight > ledHeight && peakAlphaRef.current[index] > 0) {
          canvasCtx.globalAlpha = peakAlphaRef.current[index]
          canvasCtx.fillStyle = getStreamColor(
            peakHoldRef.current[index],
            index,
          )
          canvasCtx.fillRect(
            x,
            canvasHeight - peakHeight - 2,
            actualBarWidth,
            2,
          )
          canvasCtx.globalAlpha = 1
        }
      })
    },
    [getLEDPosition, getStreamColor],
  )

  // Draw circular visualization
  const drawCircularVisualization = useCallback(
    (canvasCtx, bands, canvasWidth, canvasHeight) => {
      const centerX = canvasWidth / 2
      const centerY = canvasHeight / 2
      const maxRadius = Math.min(canvasWidth, canvasHeight) / 2 - 10
      const minRadius = 20

      // Draw background circles
      canvasCtx.strokeStyle = UNLIT_COLOR
      canvasCtx.lineWidth = 1
      for (let i = 0; i < 8; i++) {
        const radius = minRadius + (i * (maxRadius - minRadius)) / 7
        canvasCtx.beginPath()
        canvasCtx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
        canvasCtx.stroke()
      }

      // Draw frequency bands as radial segments
      bands.forEach((band, index) => {
        const amplitude = band.value
        const peakAmplitude = peakHoldRef.current[index]

        if (amplitude > 0 || peakAmplitude > 0) {
          const angle = (index / bands.length) * 2 * Math.PI
          const angleStep = (2 * Math.PI) / bands.length

          // Draw current amplitude
          if (amplitude > 0) {
            const radius = minRadius + amplitude * (maxRadius - minRadius)
            const color = getStreamColor(amplitude, index)

            canvasCtx.fillStyle = color
            canvasCtx.beginPath()
            canvasCtx.moveTo(centerX, centerY)
            canvasCtx.arc(centerX, centerY, radius, angle, angle + angleStep)
            canvasCtx.closePath()
            canvasCtx.fill()
          }

          // Draw peak hold
          if (peakAmplitude > amplitude && peakAlphaRef.current[index] > 0) {
            const peakRadius =
              minRadius + peakAmplitude * (maxRadius - minRadius)
            const peakColor = getStreamColor(peakAmplitude, index)

            canvasCtx.globalAlpha = peakAlphaRef.current[index]
            canvasCtx.strokeStyle = peakColor
            canvasCtx.lineWidth = 3
            canvasCtx.beginPath()
            canvasCtx.arc(
              centerX,
              centerY,
              peakRadius,
              angle,
              angle + angleStep,
            )
            canvasCtx.stroke()
            canvasCtx.globalAlpha = 1
          }
        }
      })
    },
    [getStreamColor],
  )

  // Initialize particles for particle system
  const initializeParticles = useCallback((width, height) => {
    if (particlesRef.current.length === 0) {
      const particleCount = 200
      for (let i = 0; i < particleCount; i++) {
        particlesRef.current.push({
          x: Math.random() * width,
          y: Math.random() * height,
          size: Math.random() * 3 + 1,
          speedX: Math.random() * 0.5 - 0.25,
          speedY: Math.random() * 0.5 - 0.25,
          hue: Math.random() * 360,
        })
      }
    }
  }, [])

  // Knight Rider scanner visualization
  const drawKnightRiderScanner = useCallback(
    (canvasCtx, bands, canvasWidth, canvasHeight) => {
      const barWidth = canvasWidth / bands.length
      const scannerPos =
        (Math.sin(timeRef.current * 0.002) * 0.5 + 0.5) * canvasWidth
      const scannerIndex = Math.floor(scannerPos / barWidth)

      // Calculate more sensitive amplitude by averaging multiple bands around scanner position
      const bandRange = Math.max(1, Math.floor(bands.length / 8)) // Use 1/8 of bands for averaging
      let totalAmplitude = 0
      let bandCount = 0

      for (
        let i = Math.max(0, scannerIndex - bandRange);
        i <= Math.min(bands.length - 1, scannerIndex + bandRange);
        i++
      ) {
        totalAmplitude += bands[i]?.value || 0
        bandCount++
      }

      const avgAmplitude = bandCount > 0 ? totalAmplitude / bandCount : 0

      // Apply sensitivity boost and minimum threshold
      const sensitivityBoost = 3.0 // Make it 3x more sensitive
      const minThreshold = 0.05 // Minimum threshold to show any effect
      const amplitude = Math.max(
        0,
        (avgAmplitude - minThreshold) * sensitivityBoost,
      )

      const centerY = canvasHeight / 2
      const maxHeight = canvasHeight * 0.4 // Use 40% of canvas height for each direction

      // Draw background grid
      canvasCtx.strokeStyle = 'rgba(0, 255, 255, 0.1)'
      canvasCtx.lineWidth = 1
      for (let i = 0; i < canvasWidth; i += 20) {
        canvasCtx.beginPath()
        canvasCtx.moveTo(i, 0)
        canvasCtx.lineTo(i, canvasHeight)
        canvasCtx.stroke()
      }

      // Draw center line
      canvasCtx.strokeStyle = 'rgba(0, 255, 255, 0.3)'
      canvasCtx.lineWidth = 1
      canvasCtx.beginPath()
      canvasCtx.moveTo(0, centerY)
      canvasCtx.lineTo(canvasWidth, centerY)
      canvasCtx.stroke()

      // Always show scanner bar, but vary intensity and size based on amplitude
      const baseIntensity = 0.3 // Always show some effect
      const dynamicIntensity = Math.min(1, amplitude)
      const totalIntensity = baseIntensity + dynamicIntensity * 0.7

      // Draw scanner bar with glow - mirrored effect
      const gradient = canvasCtx.createLinearGradient(
        scannerPos,
        0,
        scannerPos + barWidth * 2,
        0,
      )
      gradient.addColorStop(0, `rgba(0, 255, 255, ${totalIntensity})`)
      gradient.addColorStop(0.5, `rgba(255, 0, 255, ${totalIntensity})`)
      gradient.addColorStop(1, 'transparent')

      canvasCtx.fillStyle = gradient
      canvasCtx.shadowBlur = 15 + amplitude * 25 // Dynamic glow intensity
      canvasCtx.shadowColor = 'cyan'

      // Calculate height - always show some height, scale with amplitude
      const minBarHeight = maxHeight * 0.1 // Always show 10% height
      const dynamicHeight = maxHeight * amplitude
      const barHeight = minBarHeight + dynamicHeight

      // Draw upper half (from center upward)
      canvasCtx.fillRect(
        scannerPos,
        centerY - barHeight,
        barWidth * 2,
        barHeight,
      )

      // Draw lower half (from center downward) - mirrored
      canvasCtx.fillRect(scannerPos, centerY, barWidth * 2, barHeight)

      // Add a bright center highlight that pulses with amplitude
      const centerIntensity = 0.5 + amplitude * 0.5
      canvasCtx.fillStyle = `rgba(255, 255, 255, ${centerIntensity})`
      canvasCtx.shadowBlur = 20 + amplitude * 30
      canvasCtx.shadowColor = 'white'
      canvasCtx.fillRect(scannerPos, centerY - 1, barWidth * 2, 2)

      canvasCtx.shadowBlur = 0
    },
    [],
  )

  // Particle system visualization
  const drawParticleSystem = useCallback(
    (canvasCtx, bands, canvasWidth, canvasHeight) => {
      initializeParticles(canvasWidth, canvasHeight)

      canvasCtx.fillStyle = 'rgba(0, 255, 255, 0.7)'
      canvasCtx.shadowBlur = 10
      canvasCtx.shadowColor = 'cyan'

      particlesRef.current.forEach((particle, i) => {
        const bandIndex = i % bands.length
        const amplitude = bands[bandIndex]?.value || 0

        // Move particles based on frequency
        particle.x += particle.speedX + amplitude * 0.5
        particle.y += particle.speedY + amplitude * 0.5

        // Wrap around edges
        if (particle.x < 0) particle.x = canvasWidth
        if (particle.x > canvasWidth) particle.x = 0
        if (particle.y < 0) particle.y = canvasHeight
        if (particle.y > canvasHeight) particle.y = 0

        // Update hue based on amplitude
        particle.hue = (particle.hue + amplitude * 2) % 360

        // Draw particle
        canvasCtx.fillStyle = `hsla(${particle.hue}, 100%, 50%, 0.8)`
        canvasCtx.beginPath()
        canvasCtx.arc(
          particle.x,
          particle.y,
          particle.size + amplitude * 2,
          0,
          Math.PI * 2,
        )
        canvasCtx.fill()
      })

      canvasCtx.shadowBlur = 0
    },
    [initializeParticles],
  )

  // Bezier waveform visualization
  const drawBezierWaveform = useCallback(
    (canvasCtx, bands, canvasWidth, canvasHeight) => {
      const sliceWidth = canvasWidth / bands.length
      let x = 0

      canvasCtx.strokeStyle = 'rgba(0, 255, 255, 0.8)'
      canvasCtx.lineWidth = 3
      canvasCtx.shadowBlur = 15
      canvasCtx.shadowColor = 'magenta'

      canvasCtx.beginPath()
      for (let i = 0; i < bands.length; i++) {
        const amplitude = bands[i]?.value || 0
        const y = canvasHeight / 2 + (amplitude - 0.5) * canvasHeight

        if (i === 0) {
          canvasCtx.moveTo(x, y)
        } else {
          const cx = x - sliceWidth / 2
          const cy =
            canvasHeight / 2 + ((bands[i - 1]?.value || 0) - 0.5) * canvasHeight
          canvasCtx.quadraticCurveTo(cx, cy, x, y)
        }
        x += sliceWidth
      }

      canvasCtx.lineTo(canvasWidth, canvasHeight / 2)
      canvasCtx.stroke()
      canvasCtx.shadowBlur = 0
    },
    [],
  )

  // Radial spectrogram visualization
  const drawRadialSpectrogram = useCallback(
    (canvasCtx, bands, canvasWidth, canvasHeight) => {
      const centerX = canvasWidth / 2
      const centerY = canvasHeight / 2
      const radius = Math.min(centerX, centerY) * 0.8

      const angleStep = (Math.PI * 2) / bands.length
      const time = timeRef.current * 0.001

      // Draw background circles for reference
      canvasCtx.strokeStyle = 'rgba(0, 255, 255, 0.1)'
      canvasCtx.lineWidth = 1
      for (let r = 20; r <= radius; r += 20) {
        canvasCtx.beginPath()
        canvasCtx.arc(centerX, centerY, r, 0, Math.PI * 2)
        canvasCtx.stroke()
      }

      // Draw radial bars with dynamic colors and glow
      for (let i = 0; i < bands.length; i++) {
        const amplitude = bands[i]?.value || 0
        const angle = i * angleStep + time

        if (amplitude > 0) {
          const barHeight = radius * amplitude
          const endX = centerX + Math.cos(angle) * barHeight
          const endY = centerY + Math.sin(angle) * barHeight

          // Dynamic color based on frequency band and amplitude
          const hue = (i / bands.length) * 360 + time * 50 // Rotating color wheel
          const saturation = 80 + amplitude * 20 // More saturated with higher amplitude
          const lightness = 50 + amplitude * 30 // Brighter with higher amplitude

          // Create gradient for each bar
          const gradient = canvasCtx.createLinearGradient(
            centerX,
            centerY,
            endX,
            endY,
          )
          gradient.addColorStop(
            0,
            `hsla(${hue}, ${saturation}%, ${lightness}%, 0.3)`,
          )
          gradient.addColorStop(
            0.5,
            `hsla(${hue}, ${saturation}%, ${lightness}%, 0.8)`,
          )
          gradient.addColorStop(
            1,
            `hsla(${hue}, ${saturation}%, ${lightness}%, 1)`,
          )

          // Draw thick glow effect
          canvasCtx.strokeStyle = gradient
          canvasCtx.lineWidth = 4 + amplitude * 6 // Thicker bars for higher amplitude
          canvasCtx.shadowBlur = 15 + amplitude * 20 // Stronger glow for higher amplitude
          canvasCtx.shadowColor = `hsl(${hue}, 100%, 60%)`

          canvasCtx.beginPath()
          canvasCtx.moveTo(centerX, centerY)
          canvasCtx.lineTo(endX, endY)
          canvasCtx.stroke()

          // Draw bright tip for high amplitude bars
          if (amplitude > 0.5) {
            canvasCtx.fillStyle = `hsla(${hue}, 100%, 80%, 0.9)`
            canvasCtx.shadowBlur = 25
            canvasCtx.shadowColor = `hsl(${hue}, 100%, 70%)`
            canvasCtx.beginPath()
            canvasCtx.arc(endX, endY, 3 + amplitude * 4, 0, Math.PI * 2)
            canvasCtx.fill()
          }
        }
      }

      // Draw center core with pulsing effect
      const centerPulse = Math.sin(time * 3) * 0.3 + 0.7
      canvasCtx.fillStyle = `rgba(255, 255, 255, ${centerPulse * 0.8})`
      canvasCtx.shadowBlur = 30
      canvasCtx.shadowColor = 'white'
      canvasCtx.beginPath()
      canvasCtx.arc(centerX, centerY, 8 + centerPulse * 4, 0, Math.PI * 2)
      canvasCtx.fill()

      canvasCtx.shadowBlur = 0
    },
    [],
  )

  // Energy field visualization - mirrored quadrants
  const drawEnergyField = useCallback(
    (canvasCtx, bands, canvasWidth, canvasHeight) => {
      canvasCtx.globalCompositeOperation = 'lighter'
      canvasCtx.fillStyle = 'rgba(0, 255, 255, 0.1)'

      const halfWidth = canvasWidth / 2
      const halfHeight = canvasHeight / 2

      // Draw energy fields in all four quadrants with mirrored effects
      // Quadrant mapping: 0=top-left, 1=top-right, 2=bottom-left, 3=bottom-right (original)
      for (let quadrant = 0; quadrant < 4; quadrant++) {
        const startX = quadrant % 2 === 0 ? 0 : halfWidth
        const startY = quadrant < 2 ? 0 : halfHeight
        const endX = quadrant % 2 === 0 ? halfWidth : canvasWidth
        const endY = quadrant < 2 ? halfHeight : canvasHeight

        for (let y = startY; y < endY; y += 5) {
          for (let x = startX; x < endX; x += 5) {
            // Calculate relative position within quadrant
            const relX = x - startX
            const relY = y - startY

            // Mirror coordinates for different quadrants (original is quadrant 3 - bottom-right)
            let mirroredX = relX
            let mirroredY = relY

            if (quadrant === 0) {
              // Top-left: mirror both horizontally and vertically from bottom-right
              mirroredX = halfWidth - 1 - relX
              mirroredY = halfHeight - 1 - relY
            } else if (quadrant === 1) {
              // Top-right: mirror vertically from bottom-right
              mirroredX = relX
              mirroredY = halfHeight - 1 - relY
            } else if (quadrant === 2) {
              // Bottom-left: mirror horizontally from bottom-right
              mirroredX = halfWidth - 1 - relX
              mirroredY = relY
            }
            // Quadrant 3 (bottom-right) is the original - no mirroring needed

            const bandIndex = Math.floor((mirroredX / halfWidth) * bands.length)
            const amplitude = bands[bandIndex]?.value || 0
            const distortion = amplitude * 10
            const size = 2 + distortion

            // Add quadrant-specific phase offset for variety
            const phaseOffset = (quadrant * Math.PI) / 2

            canvasCtx.beginPath()
            canvasCtx.arc(
              x +
                Math.sin(
                  timeRef.current * 0.001 + mirroredX * 0.01 + phaseOffset,
                ) *
                  10,
              y +
                Math.cos(
                  timeRef.current * 0.001 + mirroredY * 0.01 + phaseOffset,
                ) *
                  10,
              size,
              0,
              Math.PI * 2,
            )
            canvasCtx.fill()
          }
        }
      }

      canvasCtx.globalCompositeOperation = 'source-over'
    },
    [],
  )

  // 3D tunnel effect visualization
  const draw3DTunnel = useCallback(
    (canvasCtx, bands, canvasWidth, canvasHeight) => {
      const centerX = canvasWidth / 2
      const centerY = canvasHeight / 2
      const maxRadius = Math.sqrt(centerX * centerX + centerY * centerY)

      const bass = bands[0]?.value || 0
      const time = timeRef.current * 0.001 + bass

      for (let r = maxRadius; r > 0; r -= 5) {
        const bandIndex = Math.floor(r % bands.length)
        const amplitude = bands[bandIndex]?.value || 0
        const angle = time + (maxRadius - r) * 0.01
        const size = 2 + amplitude * 3

        canvasCtx.strokeStyle = `hsl(${angle * 50}, 100%, 50%)`
        canvasCtx.lineWidth = size

        canvasCtx.beginPath()
        canvasCtx.arc(centerX, centerY, r, angle, angle + Math.PI * 0.1)
        canvasCtx.stroke()
      }
    },
    [],
  )

  // Neon grid visualization
  const drawNeonGrid = useCallback(
    (canvasCtx, bands, canvasWidth, canvasHeight) => {
      canvasCtx.strokeStyle = 'rgba(0, 255, 255, 0.3)'
      canvasCtx.lineWidth = 1

      const gridSize = 20
      const cols = Math.floor(canvasWidth / gridSize)
      const rows = Math.floor(canvasHeight / gridSize)

      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const bandIndex = (x + y) % bands.length
          const amplitude = bands[bandIndex]?.value || 0
          const dist = amplitude * 5
          const offsetX = Math.sin(timeRef.current * 0.002 + x * 0.1) * dist
          const offsetY = Math.cos(timeRef.current * 0.002 + y * 0.1) * dist

          canvasCtx.beginPath()
          canvasCtx.rect(
            x * gridSize + offsetX,
            y * gridSize + offsetY,
            gridSize,
            gridSize,
          )
          canvasCtx.stroke()
        }
      }

      // Add scan lines
      canvasCtx.fillStyle = 'rgba(0, 0, 0, 0.1)'
      for (let y = 0; y < canvasHeight; y += 2) {
        canvasCtx.fillRect(0, y, canvasWidth, 1)
      }
    },
    [],
  )

  // Fire/plasma effect visualization - mirrored quadrants
  const drawFireEffect = useCallback(
    (canvasCtx, bands, canvasWidth, canvasHeight) => {
      const imageData = canvasCtx.createImageData(canvasWidth, canvasHeight)
      const data = imageData.data

      const halfWidth = canvasWidth / 2
      const halfHeight = canvasHeight / 2

      for (let y = 0; y < canvasHeight; y++) {
        for (let x = 0; x < canvasWidth; x++) {
          const i = (x + y * canvasWidth) * 4

          // Determine which quadrant this pixel is in
          // Quadrant mapping: 0=top-left, 1=top-right, 2=bottom-left, 3=bottom-right (original)
          const quadrant = (x >= halfWidth ? 1 : 0) + (y >= halfHeight ? 2 : 0)

          // Calculate mirrored coordinates within the quadrant (original is quadrant 3 - bottom-right)
          let mirroredX = x % halfWidth
          let mirroredY = y % halfHeight

          if (quadrant === 0) {
            // Top-left: mirror both horizontally and vertically from bottom-right
            mirroredX = halfWidth - 1 - mirroredX
            mirroredY = halfHeight - 1 - mirroredY
          } else if (quadrant === 1) {
            // Top-right: mirror vertically from bottom-right
            mirroredY = halfHeight - 1 - mirroredY
          } else if (quadrant === 2) {
            // Bottom-left: mirror horizontally from bottom-right
            mirroredX = halfWidth - 1 - mirroredX
          }
          // Quadrant 3 (bottom-right) is the original - no mirroring needed

          const bandIndex = Math.floor((mirroredX / halfWidth) * bands.length)
          const amplitude = bands[bandIndex]?.value || 0

          // Fire-like color gradient - using blue-purple scheme for all quadrants
          // Blue-purple "cool fire" effect
          const r = 100 * amplitude
          const g = 50 * amplitude
          const b = 255 * amplitude

          data[i] = r
          data[i + 1] = g
          data[i + 2] = b

          // Fade out based on distance from center of each quadrant
          const centerX = halfWidth / 2
          const centerY = halfHeight / 2
          const distFromCenter = Math.sqrt(
            Math.pow(mirroredX - centerX, 2) + Math.pow(mirroredY - centerY, 2),
          )
          const maxDist = Math.sqrt(centerX * centerX + centerY * centerY)
          data[i + 3] = 255 * (1 - distFromCenter / maxDist) * amplitude
        }
      }

      canvasCtx.putImageData(imageData, 0, 0)
    },
    [],
  )

  // Start the LED bars visualization
  const startVisualization = useCallback(() => {
    if (!analyserRef.current || !canvasRef.current) {
      return
    }

    const analyser = analyserRef.current
    const canvas = canvasRef.current
    const canvasCtx = canvas.getContext('2d')

    const bufferLength = analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)
    const sampleRate = audioContextRef.current?.sampleRate || 44100

    // Smoothing variables - optimized for dramatic mid-range visualization
    const smoothingFactor = 0.3 // More responsive to changes
    const previousData = new Float32Array(bufferLength)
    const smoothedData = new Float32Array(bufferLength)

    let lastTime = 0
    const fps = 60

    const draw = (currentTime) => {
      // Limit frame rate
      if (currentTime - lastTime < 1000 / fps) {
        animationRef.current = requestAnimationFrame(draw)
        return
      }
      lastTime = currentTime
      timeRef.current = currentTime

      animationRef.current = requestAnimationFrame(draw)

      // Only get audio data if not muted or if showing when muted
      if (!isMuted || showWhenMuted) {
        analyser.getByteFrequencyData(dataArray)
      }

      const canvasWidth = canvas.width
      const canvasHeight = canvas.height

      // Clear canvas with black background
      canvasCtx.fillStyle = '#000'
      canvasCtx.fillRect(0, 0, canvasWidth, canvasHeight)

      // Smooth the frequency data or fade to flatline when muted
      if (!isMuted || showWhenMuted) {
        for (let i = 0; i < bufferLength; i++) {
          smoothedData[i] =
            previousData[i] * smoothingFactor +
            dataArray[i] * (1 - smoothingFactor)
          previousData[i] = smoothedData[i]
        }
      } else {
        // When muted, fade the previous data to zero
        for (let i = 0; i < bufferLength; i++) {
          smoothedData[i] = previousData[i] * 0.95 // Fade to zero
          previousData[i] = smoothedData[i]
        }
      }

      // Calculate frequency bands
      const bands = calculateFrequencyBands(smoothedData, sampleRate)

      // Calculate bar dimensions
      const barWidth = canvasWidth / bands.length
      const barSpacing = barWidth * LED_SPACE_H
      const actualBarWidth = Math.max(1, barWidth - barSpacing)

      // Update peak hold system
      const deltaTime = 16.67 // ~60fps

      bands.forEach((band, index) => {
        const peakHold = peakHoldRef.current[index]
        const peakAlpha = peakAlphaRef.current[index]

        // Check for new peak
        if (band.value >= peakHold) {
          peakHoldRef.current[index] = band.value
          peakAlphaRef.current[index] = 1
        } else {
          // Update peak hold with gravity
          const fadeFrames = Math.floor(peakFadeTime / deltaTime)

          if (peakAlpha > 0) {
            const fallSpeed = (gravity / (fps * fps)) * canvasHeight
            peakHoldRef.current[index] = Math.max(0, peakHold - fallSpeed)
            peakAlphaRef.current[index] = Math.max(
              0,
              peakAlpha - 1 / fadeFrames,
            )
          }
        }
      })

      // Draw visualization based on mode
      switch (visualizationMode) {
        case 'circular':
          drawCircularVisualization(canvasCtx, bands, canvasWidth, canvasHeight)
          break
        case 'knightRider':
          drawKnightRiderScanner(canvasCtx, bands, canvasWidth, canvasHeight)
          break
        case 'particles':
          drawParticleSystem(canvasCtx, bands, canvasWidth, canvasHeight)
          break
        case 'bezier':
          drawBezierWaveform(canvasCtx, bands, canvasWidth, canvasHeight)
          break
        case 'radial':
          drawRadialSpectrogram(canvasCtx, bands, canvasWidth, canvasHeight)
          break
        case 'energy':
          drawEnergyField(canvasCtx, bands, canvasWidth, canvasHeight)
          break
        case 'tunnel':
          draw3DTunnel(canvasCtx, bands, canvasWidth, canvasHeight)
          break
        case 'grid':
          drawNeonGrid(canvasCtx, bands, canvasWidth, canvasHeight)
          break
        case 'fire':
          drawFireEffect(canvasCtx, bands, canvasWidth, canvasHeight)
          break
        default:
          drawBarVisualization(
            canvasCtx,
            bands,
            canvasWidth,
            canvasHeight,
            barWidth,
            barSpacing,
            actualBarWidth,
          )
      }
    }

    draw(0)
  }, [
    isMuted,
    showWhenMuted,
    visualizationMode,
    calculateFrequencyBands,
    drawBarVisualization,
    drawCircularVisualization,
    drawKnightRiderScanner,
    drawParticleSystem,
    drawBezierWaveform,
    drawRadialSpectrogram,
    drawEnergyField,
    draw3DTunnel,
    drawNeonGrid,
    drawFireEffect,
  ])

  // Initialize audio context and analyser
  const initializeAudio = useCallback(() => {
    if (!audioStream) {
      return
    }

    try {
      // Create audio context if not exists
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)()
      }

      // Create analyser
      const analyser = audioContextRef.current.createAnalyser()

      // Configure analyser for speech detection
      const analyserConfig = {
        fftSize: 2048,
        minDecibels: -100,
        maxDecibels: -25, // Balanced between original (-30) and more sensitive (-20)
        smoothingTimeConstant: 0.2,
      }
      Object.assign(analyser, analyserConfig)

      // Create media stream source
      const sourceNode =
        audioContextRef.current.createMediaStreamSource(audioStream)
      sourceNode.connect(analyser)

      // Store references
      analyserRef.current = analyser
      sourceNodeRef.current = sourceNode

      // Start visualization
      startVisualization()
    } catch (error) {
      console.warn('Could not initialize audio visualizer:', error)
    }
  }, [audioStream, startVisualization])

  // Handle audio stream changes
  useEffect(() => {
    // Cleanup previous connections
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect()
      sourceNodeRef.current = null
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }

    // Initialize with new stream
    if (audioStream) {
      initializeAudio()
    }

    return () => {
      if (sourceNodeRef.current) {
        sourceNodeRef.current.disconnect()
        sourceNodeRef.current = null
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
    }
  }, [audioStream, initializeAudio])

  // Handle mute state changes
  useEffect(() => {
    // Always keep visualization running - it will handle mute state internally
    if (!animationRef.current && audioStream) {
      startVisualization()
    }
  }, [isMuted, showWhenMuted, audioStream, startVisualization])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sourceNodeRef.current) {
        sourceNodeRef.current.disconnect()
        sourceNodeRef.current = null
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
      if (audioContextRef.current) {
        audioContextRef.current.close()
        audioContextRef.current = null
      }
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className={`audio-visualizer ${className}`}
      width={width}
      height={height}
      aria-label={`${isCoachStream ? 'Coach' : 'Local'} audio visualization`}
    />
  )
}

export default AudioVisualizer
